import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import OrganisationLayout from "../layout/OrganisationLayout";
import RegisterChart from "../../Components/common/RegisterChart";
import ActivityChart from "../../Components/common/ActivityChart";
import CustomCarousel from "../../Components/common/CustomCarousel";
import OrganizationStatsChart from "../../Components/common/OrganizationStatsChart";
import { dropdownGroupsDashboard } from "../../Constant/ConstantData";
import {
  organisationDashboardActivityAction,
  organisationDashboardCategoryAction,
  organisationDashboardTrendingTagsAction,
  organisationDashboardUserStatsAction,
  organisationDashboardVideoAction,
  organisationDashboardRecentClassesAction,
  organisationDashboardRecentCollectionsAction,
  organisationDashboardRecentActiveStaffAction,
  organisationDashboardStaffStatsAction,
  organisationDashboardCategoryPerSchoolAction,
  organisationDashboardUsageHoursAction,
  organisationListAction,
} from "../../Redux/Action/OrganisationAction";

const FilterIcon = memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
  >
    <path
      d="M1.75 4.08342C1.75 2.95583 2.66408 2.04175 3.79167 2.04175C4.91925 2.04175 5.83333 2.95583 5.83333 4.08342C5.83333 5.211 4.91925 6.12508 3.79167 6.12508C2.66408 6.12508 1.75 5.211 1.75 4.08342ZM11.6667 4.66675H7V3.50008H11.6667V4.66675ZM8.16667 9.91675C8.16667 8.78917 9.08075 7.87508 10.2083 7.87508C11.3359 7.87508 12.25 8.78917 12.25 9.91675C12.25 11.0443 11.3359 11.9584 10.2083 11.9584C9.08075 11.9584 8.16667 11.0443 8.16667 9.91675ZM7 9.33342V10.5001H2.33333V9.33342H7Z"
      fill="#2B3674"
    />
  </svg>
));

const NoDataMessage = memo(({ message, style = {} }) => (
  <div
    className="w-100 d-flex justify-content-center align-items-center"
    style={{
      flex: 1,
      minHeight: "317px",
      color: "#A3AED0",
      fontSize: "14px",
      ...style,
    }}
  >
    <p
      className="m-0"
      style={{
        maxWidth: "450px",
        textAlign: "center",
        width: "100%",
        paddingTop: style.paddingTop || "0px",
      }}
    >
      {message || (
        <>
          We couldn&apos;t find any activity for the selected date range. Try
          changing your filters and check again.
        </>
      )}
    </p>
  </div>
));

const ComingSoonMessage = memo(({ message = "Coming soon" }) => (
  <div
    className="w-100 d-flex justify-content-center align-items-center"
    style={{
      height: "317px",
      color: "#A3AED0",
      fontSize: "18px",
      fontWeight: "600",
    }}
  >
    <p
      className="m-0"
      style={{
        maxWidth: "450px",
        textAlign: "center",
        width: "100%",
      }}
    >
      {message}
    </p>
  </div>
));

const LoadingMessage = memo(({ message = "Loading dashboard data...", style = {} }) => (
  <div
    className="w-100 d-flex justify-content-center align-items-center"
    style={{
      height: "317px",
      color: "#A3AED0",
      fontSize: "14px",
      ...style,
    }}
  >
    <p
      className="m-0"
      style={{
        maxWidth: "450px",
        textAlign: "center",
        width: "100%",
        paddingTop: "40px",
      }}
    >
      {message}
    </p>
  </div>
));

const fallbackThumb = require("../../Assets/Images/clogo.png");

const staticOrgSharedVideos = [
  // { organisation: "Northgate College", videos: ["Stay Safe Online", "Cyber Bullying 101", "Digital footprint"] },
  // { organisation: "Riverside School", videos: ["Mental Health Basics", "Peer Support", "Anxiety Tips"] },
  // { organisation: "Elmwood Academy", videos: ["Study Habits", "Exam Prep", "Note Taking"] },
  // { organisation: "Oakwood High", videos: ["Social Skills", "Bullying Prevention", "Online Safety"] },
];

const sortClassOptions = {
  updatedAt: "Recently updated",
  createdAt: "Recently created",
  withMostCollections: "Most shared collections",
};

const sortCollectionOptions = {
  shareDate: "Recently shared",
  createdAt: "Recently created",
  updatedAt: "Recently updated",
  mostShared: "Most shared",
};

function OrganisationGroupsDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const {
    organisationDashboardUserStats,
    organisationDashboardUserStatsStatus,
    organisationDashboardCategoryView,
    organisationDashboardCategoryViewStatus,
    organisationDashboardVideoView,
    organisationDashboardVideoViewStatus,
    organisationDashboardActivity,
    organisationDashboardActivityStatus,
    organisationDashboardTrendingTags,
    organisationDashboardTrendingTagsStatus,
    organisationDashboardRecentClasses,
    organisationDashboardRecentClassesStatus,
    organisationDashboardRecentCollections,
    organisationDashboardRecentCollectionsStatus,
    organisationDashboardRecentActiveStaff,
    organisationDashboardRecentActiveStaffStatus,
    organisationDashboardStaffStats,
    organisationDashboardStaffStatsStatus,
    organisationDashboardCategoryPerSchool,
    organisationDashboardCategoryPerSchoolStatus,
    organisationDashboardUsageHours,
    organisationDashboardUsageHoursStatus,
    organisationDashboardError,
    organisationsList,
  } = useSelector((state) => state?.organisationSlice);
  const organisationGroup = userData?.organisationGroupId;
  const [activeRole, setActiveRole] = useState("Learners");
  const [selectedClassForCategory, setSelectedClassForCategory] = useState("");
  const [sortBy, setSortBy] = useState("views");
  const [sortTags, setSortTags] = useState("views");
  const [sortClasses, setSortClasses] = useState("updatedAt");
  const [sortCollections, setSortCollections] = useState("shareDate");
  const [sortOrder, setSortOrder] = useState("asc");
  const [categoryPayload, setCategoryPayload] = useState({});
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getDefaultDateRange = () => {
    const today = new Date();
    const twelveMonthsAgo = new Date(
      today.getFullYear(),
      today.getMonth() - 12,
      today.getDate(),
    );
    return [twelveMonthsAgo, today];
  };

  const [selectedFilterDateRange, setSelectedFilterDateRange] =
    useState(getDefaultDateRange);

  const learnerAccessible = true;
  const filteredGroups = dropdownGroupsDashboard;
  const organisationSchoolId = useMemo(
    () => selectedOrganisation || userData?.schoolId?._id || userData?.schoolId || "",
    [userData, selectedOrganisation],
  );

  const type = useMemo(() => {
    return activeRole === "Learners"
      ? "Student"
      : activeRole === "Parents"
        ? "Parent"
        : "Staff";
  }, [activeRole]);

  const formatDate = useCallback((date) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }, []);

  const dateRange = useMemo(() => {
    const dates =
      selectedFilterDateRange[0] && selectedFilterDateRange[1]
        ? selectedFilterDateRange
        : getDefaultDateRange();
    return {
      startDate: formatDate(dates[0]),
      endDate: formatDate(dates[1]),
    };
  }, [selectedFilterDateRange, formatDate]);

  const teacherStatsData = useMemo(() => {
    const rawData = organisationDashboardStaffStats?.dates || [];
    if (rawData.length > 0) {
      return rawData.map((item) => {
        const dateObj = new Date(item.date);
        const formattedDate = `${String(dateObj.getDate()).padStart(2, "0")}/${String(dateObj.getMonth() + 1).padStart(2, "0")}`;

        const entry = { date: formattedDate };
        item.schools?.forEach((school) => {
          entry[school.schoolName] = school.count;
        });
        return entry;
      });
    }
    return [];
  }, [organisationDashboardStaffStats]);

  const usageHoursData = useMemo(() => {
    return organisationDashboardUsageHours?.length > 0
      ? organisationDashboardUsageHours.map((item) => ({
        organisation: item.schoolName,
        staff: item.staff || 0,
        parents: item.parents || 0,
        learners: item.learners || 0,
      }))
      : [];
  }, [organisationDashboardUsageHours]);

  const basePayload = useMemo(() => {
    const typeMapping = {
      Learners: "Student",
      Parents: "Parent",
      Staff: "",
    };

    const payload = {
      ...dateRange,
      type: typeMapping[activeRole] || "",
      ...(organisationSchoolId && { schoolId: organisationSchoolId }),
      ...(categoryPayload?.group && { group: Number(categoryPayload.group) }),
      ...(categoryPayload?.year && { year: Number(categoryPayload.year) }),
    };
    return payload;
  }, [activeRole, categoryPayload, dateRange, organisationSchoolId]);

  const handleFilterDateRangeChange = useCallback((dates) => {
    setSelectedFilterDateRange(dates || getDefaultDateRange());
  }, []);

  const getGroupName = useCallback((id) => {
    const groupNames = {
      1: "KS2 Lower",
      2: "KS2 Higher",
      3: "KS3",
      4: "KS4",
      14: "College",
    };
    return groupNames[id] || id;
  }, []);

  const handleSelectForCategory = useCallback(
    (eventKey) => {
      if (typeof eventKey !== "string") {
        setSelectedClassForCategory("");
        setCategoryPayload({});
        return;
      }

      if (eventKey.startsWith("group-")) {
        const groupId = eventKey.replace("group-", "");
        setSelectedClassForCategory(getGroupName(groupId));
        setCategoryPayload({ group: groupId });
      } else if (eventKey.startsWith("year-")) {
        const yearValue = eventKey.replace("year-", "");
        setSelectedClassForCategory(`year-${yearValue}`);
        setCategoryPayload({ year: yearValue });
      }
    },
    [getGroupName],
  );

  const getOrganisationDashboardUserStatsApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardUserStatsAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardCategoryApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardCategoryAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardVideoApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardVideoAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardActivityApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardActivityAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardTrendingTagsApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardTrendingTagsAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardRecentCollectionsApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardRecentCollectionsAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardRecentActiveStaffApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardRecentActiveStaffAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardRecentClassesApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardRecentClassesAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardStaffStatsApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardStaffStatsAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardCategoryPerSchoolApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardCategoryPerSchoolAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  const getOrganisationDashboardUsageHoursApi = useCallback(
    async (payload) => {
      const response = await dispatch(
        organisationDashboardUsageHoursAction(payload),
      );
      return response;
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(organisationListAction({ page: 0, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      try {
        if (activeRole === "Learners" || activeRole === "Parents") {
          await Promise.all([
            getOrganisationDashboardUserStatsApi(basePayload),
            getOrganisationDashboardCategoryApi(basePayload),
            getOrganisationDashboardActivityApi(basePayload),
            getOrganisationDashboardTrendingTagsApi({
              ...basePayload,
              sortBy: sortTags,
            }),
            getOrganisationDashboardVideoApi({
              ...basePayload,
              sortBy,
            }),
          ]);
          return;
        }

        if (activeRole === "Staff") {
          await Promise.all([
            getOrganisationDashboardVideoApi({
              ...basePayload,
              sortBy,
            }),
            getOrganisationDashboardRecentClassesApi({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              sortBy: sortClasses,
              sortOrder,
              limit: 10,
              schoolId: organisationSchoolId,
            }),
            getOrganisationDashboardRecentCollectionsApi({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              sortBy: sortCollections,
              sortOrder,
              limit: 10,
              schoolId: organisationSchoolId,
            }),
            getOrganisationDashboardRecentActiveStaffApi({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              sortOrder,
              schoolId: organisationSchoolId,
            }),
          ]);
          return;
        }

        if (activeRole === "Organizations") {
          await Promise.all([
            getOrganisationDashboardStaffStatsApi({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              schoolId: organisationSchoolId,
            }),
            getOrganisationDashboardCategoryPerSchoolApi({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              schoolId: organisationSchoolId,
            }),
            getOrganisationDashboardUsageHoursApi({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              schoolId: organisationSchoolId,
            }),
          ]);
          return;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    activeRole,
    basePayload,
    getOrganisationDashboardActivityApi,
    getOrganisationDashboardCategoryApi,
    getOrganisationDashboardTrendingTagsApi,
    getOrganisationDashboardUserStatsApi,
    getOrganisationDashboardVideoApi,
    getOrganisationDashboardRecentClassesApi,
    getOrganisationDashboardRecentCollectionsApi,
    getOrganisationDashboardRecentActiveStaffApi,
    getOrganisationDashboardStaffStatsApi,
    getOrganisationDashboardCategoryPerSchoolApi,
    getOrganisationDashboardUsageHoursApi,
    sortBy,
    sortTags,
    sortClasses,
    sortCollections,
    sortOrder,
    organisationSchoolId,
  ]);

  const isDateFilter =
    selectedFilterDateRange &&
    selectedFilterDateRange.length > 0 &&
    selectedFilterDateRange[0] &&
    selectedFilterDateRange[1];

  const registerData = Array.isArray(organisationDashboardUserStats)
    ? organisationDashboardUserStats
    : [];
  const categoryData = Array.isArray(organisationDashboardCategoryView)
    ? organisationDashboardCategoryView
    : [];
  const activityData = useMemo(() => {
    const rawData = Array.isArray(organisationDashboardActivity)
      ? organisationDashboardActivity
      : [];
    return [...rawData].sort((a, b) => {
      const yearA = parseInt(a.year || 0, 10);
      const yearB = parseInt(b.year || 0, 10);
      return yearA - yearB;
    });
  }, [organisationDashboardActivity]);
  const trendingTags = Array.isArray(organisationDashboardTrendingTags)
    ? organisationDashboardTrendingTags
    : [];
  const videoData = Array.isArray(organisationDashboardVideoView)
    ? organisationDashboardVideoView
    : [];

  const recentClasses = Array.isArray(organisationDashboardRecentClasses?.classes)
    ? organisationDashboardRecentClasses.classes
    : Array.isArray(organisationDashboardRecentClasses)
      ? organisationDashboardRecentClasses
      : [];

  const totalClassesCount = organisationDashboardRecentClasses?.totalClasses ?? 0;

  const recentCollections = Array.isArray(organisationDashboardRecentCollections?.collections)
    ? organisationDashboardRecentCollections.collections
    : [];

  const totalCollectionsCount = organisationDashboardRecentCollections?.totalCollections ?? 0;

  const recentStaff = Array.isArray(organisationDashboardRecentActiveStaff?.staff)
    ? organisationDashboardRecentActiveStaff.staff
    : [];

  const totalStaffCount = organisationDashboardRecentActiveStaff?.totalStaff ?? 0;

  const showOrganisationDashboardError =
    !isLoading &&
    Boolean(
      organisationDashboardError &&
      [
        organisationDashboardUserStatsStatus,
        organisationDashboardCategoryViewStatus,
        organisationDashboardVideoViewStatus,
        organisationDashboardActivityStatus,
        organisationDashboardTrendingTagsStatus,
        organisationDashboardRecentClassesStatus,
        organisationDashboardRecentCollectionsStatus,
        organisationDashboardRecentActiveStaffStatus,
      ].includes("failed"),
    );

  return (
    <OrganisationLayout>
      <Container fluid>
        <Row>
          <Col lg={12} className="d-flex align-items-center">
            <div className="heading-top pe-0 ps-0 w-100">
              <p>Dashboard</p>
              <div className="back-page-title">
                <h2 style={{ fontSize: "1.5rem" }}>
                  {organisationGroup?.name || "Statistics"}
                </h2>
              </div>
              <div className="new-filter-tab-sec d-flex justify-content-between align-items-center w-100">
                <div className="new-tabs d-flex gap-2 mt-3">
                  {learnerAccessible && (
                    <button
                      className={`dash1 ${activeRole === "Learners" ? "active" : ""}`}
                      onClick={() => setActiveRole("Learners")}
                    >
                      Learners
                    </button>
                  )}
                  <button
                    className={`dash1 ${activeRole === "Parents" ? "active" : ""}`}
                    onClick={() => setActiveRole("Parents")}
                  >
                    Parents
                  </button>
                  <button
                    className={`dash1 ${activeRole === "Staff" ? "active" : ""}`}
                    onClick={() => setActiveRole("Staff")}
                  >
                    Staff
                  </button>
                  <button
                    className={`dash1 ${activeRole === "Organizations" ? "active" : ""}`}
                    onClick={() => setActiveRole("Organizations")}
                  >
                    Organisations
                  </button>
                </div>
                <div className="new-filter-sec d-flex gap-3">
                  <DatePicker
                    selected={selectedFilterDateRange[0]}
                    onChange={handleFilterDateRangeChange}
                    startDate={selectedFilterDateRange[0]}
                    endDate={selectedFilterDateRange[1]}
                    selectsRange
                    dateFormat="dd/MM/yyyy"
                    showIcon
                    placeholderText="Select Date Range"
                    isClearable
                    className="calendar-filter"
                    wrapperClassName="datepicker-wrapper"
                    customInput={<input style={{ width: "300px", padding: "12px 15px 12px 40px", borderRadius: "10px", border: "1px solid #BDE3FF", minHeight: "45px", fontSize: "16px", fontWeight: "500", color: "#2B3674" }} />}
                    maxDate={new Date()}
                  // minDate={organisationGroup?.createdAt ? new Date(organisationGroup.createdAt) : undefined}
                  />
                  <Dropdown onSelect={setSelectedOrganisation}>
                    <Dropdown.Toggle
                      variant=""
                      id="dropdown-organisation"
                      className="d-flex align-items-center organisation-filter-pill organisation-filter-dropdown"
                    >
                      <FilterIcon />
                      <p className="m-0 flex-grow-1">
                        {(Array.isArray(organisationsList) ? organisationsList : []).find(org => org._id === selectedOrganisation)?.schoolName || "All Organisations"}
                      </p>
                      {selectedOrganisation && (
                        <FaTimes
                          style={{ cursor: "pointer", marginLeft: 8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrganisation(null);
                          }}
                          title="Clear filter"
                        />
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item eventKey={null}>All Organisations</Dropdown.Item>
                      {Array.isArray(organisationsList) && organisationsList.map((org) => (
                        <Dropdown.Item key={org._id} eventKey={org._id}>
                          {org.schoolName}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown onSelect={handleSelectForCategory}>
                    <Dropdown.Toggle
                      variant=""
                      id="dropdown-basic"
                      className="d-flex align-items-center"
                    >
                      <FilterIcon />
                      <p className="m-0 flex-grow-1">
                        {selectedClassForCategory || "Year Group"}
                      </p>

                      {selectedClassForCategory && (
                        <FaTimes
                          style={{ cursor: "pointer", marginLeft: 8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectForCategory(null);
                          }}
                          title="Clear filter"
                        />
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {filteredGroups.map((group) => (
                        <React.Fragment key={group.group}>
                          <Dropdown.Item eventKey={`group-${group.group}`}>
                            {group.label}
                          </Dropdown.Item>
                          {group.years.map((year) => (
                            <Dropdown.Item
                              key={year.value}
                              eventKey={`year-${year.value}`}
                            >
                              {year.name}
                            </Dropdown.Item>
                          ))}
                        </React.Fragment>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {activeRole === "Organizations" ? (
          <>
            <Row className="mb-4">
              <Col xxl={6} lg={12} className="mt-4">
                <div className="expert-stat-card shadow-sm flex-column align-items-start" style={{ background: "#ffffff66", borderRadius: "20px", height: "100%", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <h2 className="f-24 mb-4" style={{ color: "#2B3674", fontWeight: "700" }}>Usage</h2>
                  <div style={{ width: "100%", height: "350px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {isLoading ? (
                        <LoadingMessage />
                      ) : showOrganisationDashboardError ? (
                        <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                      ) : (
                        <BarChart
                          data={usageHoursData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                          barGap={9}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#F4F7FE"
                          />
                          <XAxis
                            dataKey="organisation"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#A3AED0", fontSize: 12 }}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#A3AED0", fontSize: 12 }}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              borderRadius: "10px",
                              border: "none",
                              boxShadow:
                                "0px 18px 40px rgba(112, 144, 176, 0.12)",
                            }}
                          />
                          <Legend iconType="circle" />
                          <Bar
                            dataKey="staff"
                            fill="#2B3674"
                            radius={[4, 4, 0, 0]}
                            name="Staff"
                            barSize={40}
                          />
                          <Bar
                            dataKey="parents"
                            fill="#D200A5"
                            radius={[4, 4, 0, 0]}
                            name="Parents"
                            barSize={40}
                          />
                          <Bar
                            dataKey="learners"
                            fill="#A3AED0"
                            radius={[4, 4, 0, 0]}
                            name="Learners"
                            barSize={40}
                          />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              </Col>
              <Col xxl={6} lg={12} className="mt-4">
                <div className="expert-stat-card shadow-sm flex-column align-items-start" style={{ background: "#ffffff66", borderRadius: "20px", height: "100%", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <h2 className="f-24 mb-4" style={{ color: "#2B3674", fontWeight: "700" }}>Number of Teacher Accounts</h2>
                  <div style={{ width: "100%", height: "350px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      {isLoading ? (
                        <LoadingMessage />
                      ) : showOrganisationDashboardError ? (
                        <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                      ) : teacherStatsData?.length > 0 ? (
                        <OrganizationStatsChart teacherStatsData={teacherStatsData} />
                      ) : (
                        <NoDataMessage />
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col xxl={6} lg={12} className="mt-2">
                <div className="expert-stat-card shadow-sm flex-column align-items-start" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <h2 className="f-24 mb-4" style={{ color: "#2B3674", fontWeight: "700" }}>Most Shared Videos</h2>
                  <div
                    className="w-100"
                    style={{
                      overflow:
                        staticOrgSharedVideos.length > 0 ? "auto" : "hidden",
                    }}
                  >
                    {staticOrgSharedVideos.length > 0 ? (
                      <table className="table table-borderless">
                        <thead>
                          <tr style={{ borderBottom: "1px solid #F4F7FE" }}>
                            <th
                              style={{
                                color: "#A3AED0",
                                fontSize: "14px",
                                fontWeight: "500",
                                paddingBottom: "15px",
                              }}
                            >
                              Member Organisation
                            </th>
                            <th
                              style={{
                                color: "#A3AED0",
                                fontSize: "14px",
                                fontWeight: "500",
                                paddingBottom: "15px",
                              }}
                            >
                              Most Shared Videos
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {staticOrgSharedVideos.map((org, idx) => (
                            <tr
                              key={idx}
                              style={{
                                borderBottom:
                                  idx !== staticOrgSharedVideos.length - 1
                                    ? "1px solid #F4F7FE"
                                    : "none",
                              }}
                            >
                              <td
                                className="py-3"
                                style={{
                                  color: "#2B3674",
                                  fontWeight: "700",
                                  fontSize: "15px",
                                }}
                              >
                                {org.organisation}
                              </td>
                              <td className="py-3">
                                <div className="d-flex flex-wrap gap-2">
                                  {org.videos.map((vid, vIdx) => (
                                    <span
                                      key={vIdx}
                                      className="badge"
                                      style={{
                                        background: "rgba(163, 174, 208, 0.15)",
                                        color: "#2B3674",
                                        fontWeight: "500",
                                        padding: "6px 12px",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      {vid}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <NoDataMessage />
                    )}
                  </div>
                </div>
              </Col>
              <Col xxl={6} lg={12} className="mt-2">
                <div className="expert-stat-card shadow-sm flex-column align-items-start" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <h2 className="f-24 mb-4" style={{ color: "#2B3674", fontWeight: "700" }}>Most Viewed Categories</h2>
                  <div
                    className="w-100"
                    style={{
                      overflow:
                        organisationDashboardCategoryPerSchool?.length > 0
                          ? "auto"
                          : "hidden",
                    }}
                  >
                    {isLoading ? (
                      <div className="text-center py-4">
                        <LoadingMessage message="Loading categories..." />
                      </div>
                    ) : organisationDashboardCategoryPerSchool?.length > 0 ? (
                      <table className="table table-borderless">
                        <thead>
                          <tr style={{ borderBottom: "1px solid #F4F7FE" }}>
                            <th
                              style={{
                                color: "#A3AED0",
                                fontSize: "14px",
                                fontWeight: "500",
                                paddingBottom: "15px",
                              }}
                            >
                              Member Organisation
                            </th>
                            <th
                              style={{
                                color: "#A3AED0",
                                fontSize: "14px",
                                fontWeight: "500",
                                paddingBottom: "15px",
                              }}
                            >
                              Top 5 Viewed Categories
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {organisationDashboardCategoryPerSchool.map(
                            (org, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  borderBottom:
                                    idx !==
                                      organisationDashboardCategoryPerSchool.length -
                                      1
                                      ? "1px solid #F4F7FE"
                                      : "none",
                                }}
                              >
                                <td
                                  className="py-3"
                                  style={{
                                    color: "#2B3674",
                                    fontWeight: "700",
                                    fontSize: "15px",
                                  }}
                                >
                                  {org.schoolName}
                                </td>
                                <td className="py-3">
                                  <div className="d-flex align-items-center gap-3">
                                    {org.topCategories?.map((cat, cIdx) => (
                                      <OverlayTrigger
                                        key={cIdx}
                                        placement="top"
                                        overlay={
                                          <Tooltip>
                                            {cat.title}: {cat.views} views
                                          </Tooltip>
                                        }
                                      >
                                        <div
                                          className="d-flex flex-column align-items-center"
                                          style={{ cursor: "pointer" }}
                                        >
                                          <img
                                            src={cat.image || fallbackThumb}
                                            alt={cat.title}
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              borderRadius: "8px",
                                              objectFit: "cover",
                                              marginBottom: "2px",
                                            }}
                                          />
                                          <span
                                            style={{
                                              fontSize: "11px",
                                              color: "#707EAE",
                                              fontWeight: "600",
                                            }}
                                          >
                                            {cat.views}
                                          </span>
                                        </div>
                                      </OverlayTrigger>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <NoDataMessage />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : activeRole !== "Staff" ? (
          <>
            <Row className="mb-3">
              <Col xxl={7} lg={12} xl={7} className="display-table mt-4">
                <div className="cmn-card-color table-cell">
                  <div className="details-chart">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>
                        {learnerAccessible
                          ? `Number of ${activeRole} Accounts`
                          : "Parents"}
                      </h2>
                    </div>
                    <div className="m-2">
                      {isLoading ? (
                        <LoadingMessage />
                      ) : showOrganisationDashboardError ? (
                        <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                      ) : registerData?.length > 0 ? (
                        <RegisterChart
                          registerData={registerData}
                          userData={userData}
                        />
                      ) : (
                        <NoDataMessage
                          message={activeRole === "Learners" ? "Details will show here once organisations have been added to your account" : undefined}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </Col>
              <Col xxl={5} lg={12} xl={5} className="mt-4 display-table">
                <div className="cmn-card-color table-cell">
                  <div className="table-box-cmn">
                    <div className="results-sec">
                      <Row>
                        <Col lg={12} className="most-viewed-categories">
                          <div>
                            <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>Most Viewed Categories</h2>
                            <div className="Review-history mt-3">
                              {isLoading ? (
                                <LoadingMessage />
                              ) : showOrganisationDashboardError ? (
                                <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                              ) : categoryData.length > 0 ? (
                                <>
                                  {categoryData.map((ele, index) => (
                                    <Row
                                      className="mb-2"
                                      key={`${ele.title}-${index}`}
                                    >
                                      <Col lg={8}>
                                        <div className="d-flex align-items-center gap-3">
                                          <img
                                            src={ele.image || fallbackThumb}
                                            alt=""
                                          />
                                          <h3>{ele.title}</h3>
                                        </div>
                                      </Col>

                                      <Col className="d-flex align-items-center justify-content-end">
                                        <p>
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="12"
                                            viewBox="0 0 18 12"
                                            fill="none"
                                          >
                                            <path
                                              d="M0.328405 5.07509C0.421391 4.96018 0.5212 4.85 0.617859 4.73792C1.87019 3.28616 3.28282 2.03596 4.95665 1.11952C5.92605 0.588744 6.94797 0.21144 8.04075 0.0627858C9.35523 -0.116013 10.6217 0.0958101 11.8502 0.586899C13.3465 1.18502 14.6589 2.08869 15.8295 3.20256C16.4743 3.81612 17.0693 4.48718 17.6628 5.15491C18.1081 5.65603 18.1067 6.31341 17.6921 6.82043C16.5094 8.26679 15.1722 9.5348 13.6004 10.5264C12.5788 11.1709 11.4936 11.6531 10.3068 11.8745C8.85378 12.1456 7.4461 11.97 6.079 11.42C4.45927 10.7684 3.07035 9.74829 1.82024 8.52419C1.294 8.00889 0.803656 7.4532 0.318565 6.89597C0.294028 6.8678 0.271153 6.83811 0.249547 6.80724C-0.115966 6.28515 -0.0717478 5.56964 0.328405 5.07509ZM8.96527 9.99372C11.1136 9.99566 12.8471 8.21568 12.8492 6.00562C12.8513 3.79623 11.1193 2.01122 8.97235 2.01023C6.82384 2.00924 5.08953 3.79021 5.08869 5.99837C5.08786 8.20942 6.81724 9.99174 8.96527 9.99372Z"
                                              fill="#A3AED0"
                                            />
                                            <path
                                              d="M8.96488 8.86177C7.42934 8.86082 6.1855 7.57373 6.18843 5.98888C6.19136 4.42086 7.44391 3.13975 8.97288 3.14097C10.5075 3.14223 11.7531 4.43157 11.7495 6.01516C11.7458 7.58493 10.4959 8.86271 8.96488 8.86177Z"
                                              fill="#A3AED0"
                                            />
                                          </svg>{" "}
                                          {ele.views} Views
                                        </p>
                                      </Col>
                                    </Row>
                                  ))}
                                </>
                              ) : (
                                <NoDataMessage />
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 align-items-stretch">
              <Col xxl={5} lg={5} className="mt-2 display-table">
                <div className="table-cell shadow-sm d-flex flex-column mb-3" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>User Activity</h2>
                  <div className="total-views-chat mt-4 flex-grow-1">
                    {isLoading ? (
                      <LoadingMessage />
                    ) : showOrganisationDashboardError ? (
                      <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                    ) : (
                      <ActivityChart activityData={activityData} />
                    )}
                  </div>
                </div>

                <div className="table-cell shadow-sm d-flex flex-column h-100" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <div className="dash-drop-ad px-0 mb-4">
                    <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>Trending Tags</h2>
                          {trendingTags.length > 0 && (
                            <Dropdown onSelect={setSortTags}>
                              <Dropdown.Toggle
                                variant="success"
                                id="dropdown-basic"
                              >
                                <FilterIcon />
                                <p className="m-0">
                                  {sortTags.charAt(0).toUpperCase() +
                                    sortTags.slice(1)}
                                </p>
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  eventKey="views"
                                  active={sortTags === "views"}
                                >
                                  Views
                                </Dropdown.Item>
                                <Dropdown.Item
                                  eventKey="likes"
                                  active={sortTags === "likes"}
                                >
                                  Likes
                                </Dropdown.Item>
                                <Dropdown.Item
                                  eventKey="saves"
                                  active={sortTags === "saves"}
                                >
                                  Saves
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                    </div>
                    {isLoading ? (
                      <LoadingMessage />
                    ) : showOrganisationDashboardError ? (
                      <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                    ) : trendingTags.length > 0 ? (
                      <Row className="mt-2 mx-auto">
                        {trendingTags.map(({ _id, title }) => (
                          <OverlayTrigger
                            key={_id}
                            placement="top"
                            overlay={
                              <Tooltip
                                id={`tooltip-${_id}`}
                                className="tooltip"
                              >
                                Browse {title} Videos
                              </Tooltip>
                            }
                          >
                            <Col
                              lg={3}
                              md={3}
                              sm={4}
                              className="mb-3"
                              style={{ cursor: "pointer" }}
                              // onClick={() =>
                              //   navigate(`/browse?searchkey=${title}`)
                              // }
                              role="button"
                              tabIndex={0}
                            // onKeyPress={(e) => {
                            //   if (e.key === "Enter" || e.key === " ") {
                            //     navigate(`/browse?searchkey=${title}`);
                            //   }
                            // }}
                            >
                              <div className="user-activity-card">
                                <div
                                  className="user-details mx-2"
                                  style={{ flex: 1, minWidth: 0 }}
                                >
                                  <h6
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    #{title}
                                  </h6>
                                </div>
                              </div>
                            </Col>
                          </OverlayTrigger>
                        ))}
                      </Row>
                    ) : (
                      <NoDataMessage />
                    )}
                </div>
              </Col>

              <Col xxl={7} lg={7} className="mt-2 display-table">
                <div className="table-cell shadow-sm d-flex flex-column h-100" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <div className="dash-drop-ad mb-4 px-0">
                    <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>Most Shared Videos</h2>
                    <Dropdown onSelect={setSortBy}>
                      <Dropdown.Toggle
                        variant="success"
                        id="dropdown-basic"
                      >
                        <FilterIcon />{" "}
                        <p className="m-0">
                          {sortBy.charAt(0).toUpperCase() +
                            sortBy.slice(1)}
                        </p>
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          eventKey="views"
                          active={sortBy === "views"}
                        >
                          Views
                        </Dropdown.Item>
                        <Dropdown.Item
                          eventKey="likes"
                          active={sortBy === "likes"}
                        >
                          Likes
                        </Dropdown.Item>
                        <Dropdown.Item
                          eventKey="saves"
                          active={sortBy === "saves"}
                        >
                          Saves
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="Review-history mt-3 flex-grow-1 h-100">
                    {isLoading ? (
                      <LoadingMessage style={{ height: "704px" }} />
                    ) : showOrganisationDashboardError ? (
                      <LoadingMessage message="We couldn't load organisation dashboard data right now." />
                    ) : videoData.length > 0 ? (
                      <CustomCarousel
                        videoData={videoData}
                        type="Staff"
                        redirection={true}
                      />
                    ) : (
                      <NoDataMessage style={{ minHeight: "704px" }} />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <>

            <Row className="mb-3">
              <Col xxl={7} lg={12} xl={7} className="display-table mt-4">
                <div className="table-cell shadow-sm" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", padding: "15px 20px" }}>
                  <div className="recently-active-chart">
                    <div className="recently-main-sec d-flex justify-content-between align-items-center mb-4">
                      <div className="recently-head">
                        <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>Recently Active Classes</h2>
                        {isDateFilter ? (
                          recentClasses.length > 0 && (
                            <div className="total-class-head mt-1">
                              <p className="m-0" style={{ color: "#A3AED0", fontSize: "14px" }}>
                                {recentClasses.length} Active classes during this period
                              </p>
                            </div>
                          )
                        ) : (
                          recentClasses.length > 0 && (
                            <div className="total-class-head mt-1">
                              <p className="m-0" style={{ color: "#A3AED0", fontSize: "14px" }}>
                                {recentClasses.length} classes total
                              </p>
                            </div>
                          )
                        )}
                      </div>

                      <div className="dash-drop-ad d-flex align-items-center gap-3">
                        <Dropdown onSelect={setSortClasses}>
                          <Dropdown.Toggle
                            variant="success"
                            id="dropdown-classes-sort"
                            className="d-flex align-items-center gap-2"
                            style={{ background: "#F4F7FE", border: "none", color: "#2B3674", borderRadius: "12px", padding: "8px 16px", fontSize: "14px", fontWeight: "600" }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M6 12h12m-9 6h6" />
                            </svg>
                            {sortClassOptions[sortClasses] || "Recently updated"}
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
                            <Dropdown.Item eventKey="updatedAt">Recently updated</Dropdown.Item>
                            <Dropdown.Item eventKey="createdAt">Recently created</Dropdown.Item>
                            <Dropdown.Item eventKey="withMostCollections">Most shared</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                    <div className="recently-card">
                      <Row>
                        <Col lg={12}>
                          <Row>
                            {isLoading || organisationDashboardRecentClassesStatus === "loading" ? (
                              <Col lg={12}><LoadingMessage message="Fetching recent classes..." /></Col>
                            ) : recentClasses.length > 0 ? (
                              recentClasses.map((item) => (
                                <Col lg={4} md={6} sm={12} className="mb-3" key={item._id}>
                                  <div className="recently-active-card h-100 shadow-sm">
                                    <div className="recently-active-head card-body">
                                      <h6 className="card-title" title={item.name} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", wordBreak: "break-all", color: "rgb(43, 54, 116)", fontWeight: "600", fontSize: "16px", lineHeight: "26px", letterSpacing: "-0.02em" }}>
                                        {item.name}
                                      </h6>
                                    </div>
                                    <div className="year-section">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                        <path fill="#2b3674" d="M21.5 16.925v-7.05L11.975 15 1 9 11.975 3 23 9v7.925h-1.5ZM11.975 21l-7.25 -3.975v-5.25l7.25 3.975 7.25 -3.975v5.25L11.975 21Z" />
                                      </svg>
                                      <div className="year-desc"><p>Year {item.year}</p></div>
                                    </div>
                                    <div className="owned-section">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                        <path fill="#2b3674" d="M3.5 20c-0.4 0 -0.75 -0.15415 -1.05 -0.4625C2.15 19.22915 2 18.88335 2 18.5V5.5c0 -0.38335 0.15 -0.729165 0.45 -1.0375C2.75 4.154165 3.1 4 3.5 4h7.025l1.5 1.5H20.5c0.38335 0 0.72915 0.15415 1.0375 0.4625 0.30835 0.30835 0.4625 0.65415 0.4625 1.0375v11.5c0 0.38335 -0.15415 0.72915 -0.4625 1.0375 -0.30835 0.30835 -0.65415 0.4625 -1.0375 0.4625H3.5Zm7.475 -3.25h8v-0.475c0 -0.7 -0.35415 -1.27085 -1.0625 -1.7125 -0.70835 -0.44165 -1.6875 -0.6625 -2.9375 -0.6625s-2.22915 0.22085 -2.9375 0.6625c-0.70835 0.44165 -1.0625 1.0125 -1.0625 1.7125v0.475Zm4 -4.35c0.5 0 0.92915 -0.17915 1.2875 -0.5375 0.35835 -0.35835 0.5375 -0.7875 0.5375 -1.2875 0 -0.5 -0.17915 -0.92915 -0.5375 -1.2875 -0.35835 -0.35835 -0.7875 -0.5375 -1.2875 -0.5375 -0.5 0 -0.92915 0.17915 -1.2875 0.5375 -0.35835 0.35835 -0.5375 0.7875 -0.5375 1.2875 0 0.5 0.17915 0.92915 0.5375 1.2875 0.35835 0.35835 0.7875 0.5375 1.2875 0.5375Z" />
                                      </svg>
                                      <div className="year-desc">
                                        <p>
                                          Owned by {item.ownerName || item.schoolId?.schoolName || item.schoolId?.name || "N/A"}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="students-section">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                        <path fill="#2b3674" d="M0.94995 20.0001v-2.35c0 -0.58335 0.15 -1.1125 0.45 -1.5875 0.3 -0.475 0.71667 -0.82915 1.25 -1.0625 1.21667 -0.53335 2.3125 -0.91665 3.2875 -1.15 0.975 -0.23335 1.97915 -0.35 3.0125 -0.35 1.03335 0 2.03335 0.11665 3 0.35 0.96665 0.23335 2.05835 0.61665 3.275 1.15 0.53335 0.23335 0.95415 0.5875 1.2625 1.0625 0.30835 0.475 0.4625 1.00415 0.4625 1.5875v2.35h-16Zm17.5 0v-2.35c0 -1.05 -0.26665 -1.9125 -0.8 -2.5875 -0.53335 -0.675 -1.23335 -1.22085 -2.1 -1.6375 1.15 0.13335 2.23335 0.32915 3.25 0.5875 1.01665 0.25835 1.84165 0.55415 2.475 0.8875 0.55 0.31665 0.98335 0.70835 1.3 1.175 0.31665 0.46665 0.475 0.99165 0.475 1.575v2.35h-4.6Zm-9.5 -8.025c-1.1 0 -2 -0.35 -2.7 -1.05 -0.7 -0.7 -1.05 -1.6 -1.05 -2.7s0.35 -2 1.05 -2.7c0.7 -0.7 1.6 -1.05 2.7 -1.05s2 0.35 2.7 1.05c0.7 0.7 1.05 1.6 1.05 2.7s-0.35 2 -1.05 2.7c-0.7 0.7 -1.6 1.05 -2.7 1.05Zm9 -3.75c0 1.1 -0.35 2 -1.05 2.7 -0.7 0.7 -1.6 1.05 -2.7 1.05 -0.18335 0 -0.3875 -0.0125 -0.6125 -0.0375 -0.225 -0.025 -0.42915 -0.07085 -0.6125 -0.1375 0.4 -0.41665 0.70415 -0.92915 0.9125 -1.5375 0.20835 -0.60835 0.3125 -1.2875 0.3125 -2.0375s-0.10415 -1.4125 -0.3125 -1.9875c-0.20835 -0.575 -0.5125 -1.10415 -0.9125 -1.5875 0.18335 -0.05 0.3875 -0.09167 0.6125 -0.125 0.225 -0.033335 0.42915 -0.05 0.6125 -0.05 1.1 0 2 0.35 2.7 1.05 0.7 0.7 1.05 1.6 1.05 2.7Z" />
                                      </svg>
                                      <div className="year-desc"><p>{item.studentId?.length || item.studentsCount || item.studentCount || 0} students</p></div>
                                    </div>
                                    <div className="shared-collections">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                        <path fill="#2b3674" d="M4 22.0001v-14.525c0 -0.4125 0.146915 -0.76565 0.44075 -1.0595 0.293665 -0.29365 0.64675 -0.4405 1.05925 -0.4405h9c0.4125 0 0.76565 0.14685 1.0595 0.4405 0.29365 0.29385 0.4405 0.647 0.4405 1.0595v14.525l-6 -2.975 -6 2.975Zm14.5 -3.975v-14.55H7.1v-1.5H18.5c0.4125 0 0.76565 0.14683 1.0595 0.4405 0.29365 0.29383 0.4405 0.647 0.4405 1.0595v14.55h-1.5Z" />
                                      </svg>
                                      <div className="year-desc"><p>{item.collectionId?.length || item.collectionsCount || item.collectionCount || 0} shared collections</p></div>
                                    </div>
                                  </div>
                                </Col>
                              ))
                            ) : (
                              <Col lg={12}><NoDataMessage /></Col>
                            )}
                          </Row>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Col>

              <Col xxl={5} lg={12} xl={5} className="mt-4 display-table">
                <div className="table-cell shadow-sm" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", position: "relative", padding: "15px 20px" }}>
                  <div className="recently-active-chart">
                    <div className="recently-main-sec d-flex justify-content-between align-items-center mb-4">
                      <div className="recently-head">
                        <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>Recently Shared Collections</h2>
                        {isDateFilter ? (
                          totalCollectionsCount > 0 && (
                            <div className="total-class-head mt-1">
                              <p className="m-0" style={{ color: "#A3AED0", fontSize: "14px" }}>
                                {totalCollectionsCount} Active Collections during this period
                              </p>
                            </div>
                          )
                        ) : (
                          totalCollectionsCount > 0 && (
                            <div className="total-class-head mt-1">
                              <p className="m-0" style={{ color: "#A3AED0", fontSize: "14px" }}>
                                {totalCollectionsCount} collections total
                              </p>
                            </div>
                          )
                        )}
                      </div>

                      <div className="dash-drop-ad d-flex align-items-center gap-3">
                        <Dropdown onSelect={setSortCollections}>
                          <Dropdown.Toggle
                            variant="success"
                            id="dropdown-collections-sort"
                            className="d-flex align-items-center gap-2"
                            style={{ background: "#F4F7FE", border: "none", color: "#2B3674", borderRadius: "12px", padding: "8px 16px", fontSize: "14px", fontWeight: "600" }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M6 12h12m-9 6h6" />
                            </svg>
                            {sortCollectionOptions[sortCollections] || "Recently shared"}
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ borderRadius: "12px", border: "none", boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}>
                            <Dropdown.Item eventKey="shareDate">Recently shared</Dropdown.Item>
                            <Dropdown.Item eventKey="updatedAt">Recently updated</Dropdown.Item>
                            <Dropdown.Item eventKey="createdAt">Recently created</Dropdown.Item>
                            <Dropdown.Item eventKey="mostShared">Most shared</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                    <div className="recently-card scrollable-card-list" style={{ maxHeight: "650px", overflowY: "auto", overflowX: "hidden", paddingRight: "5px" }}>
                      <Row className="mx-0">
                        {isLoading || organisationDashboardRecentCollectionsStatus === "loading" ? (
                          <Col lg={12}><LoadingMessage message="Fetching recent collections..." /></Col>
                        ) : recentCollections.length > 0 ? (
                          recentCollections.map((item) => (
                            <Col lg={6} md={6} sm={12} className="mb-3" key={item._id}>
                              <div className="recently-active-card h-100 shadow-sm">
                                <div className="recently-active-head card-body">
                                  <div className="d-flex align-items-center gap-2">
                                    {item.image && (
                                      <img src={item.image} alt="" style={{ width: "24px", height: "24px", objectFit: "contain" }} />
                                    )}
                                    <h6 className="card-title m-0 text-truncate" title={item.title || item.name} style={{ color: "rgb(43, 54, 116)", fontWeight: "600", fontSize: "16px", lineHeight: "26px", letterSpacing: "-0.02em" }}>
                                      {item.title || item.name}
                                    </h6>
                                  </div>
                                </div>
                                <div className="owned-section">
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                    <path fill="#2b3674" d="M3.5 20c-0.4 0 -0.75 -0.15415 -1.05 -0.4625C2.15 19.22915 2 18.88335 2 18.5V5.5c0 -0.38335 0.15 -0.729165 0.45 -1.0375C2.75 4.154165 3.1 4 3.5 4h7.025l1.5 1.5H20.5c0.38335 0 0.72915 0.15415 1.0375 0.4625 0.30835 0.30835 0.4625 0.65415 0.4625 1.0375v11.5c0 0.38335 -0.15415 0.72915 -0.4625 1.0375 -0.30835 0.30835 -0.65415 0.4625 -1.0375 0.4625H3.5Zm7.475 -3.25h8v-0.475c0 -0.7 -0.35415 -1.27085 -1.0625 -1.7125 -0.70835 -0.44165 -1.6875 -0.6625 -2.9375 -0.6625s-2.22915 0.22085 -2.9375 0.6625c-0.70835 0.44165 -1.0625 1.0125 -1.0625 1.7125v0.475Zm4 -4.35c0.5 0 0.92915 -0.17915 1.2875 -0.5375 0.35835 -0.35835 0.5375 -0.7875 0.5375 -1.2875 0 -0.5 -0.17915 -0.92915 -0.5375 -1.2875 -0.35835 -0.35835 -0.7875 -0.5375 -1.2875 -0.5375 -0.5 0 -0.92915 0.17915 -1.2875 0.5375 -0.35835 0.35835 -0.5375 0.7875 -0.5375 1.2875 0 0.5 0.17915 0.92915 0.5375 1.2875 0.35835 0.35835 0.7875 0.5375 1.2875 0.5375Z" />
                                  </svg>
                                  <div className="year-desc">
                                    <p>
                                      Owned by {item.schoolId?.schoolName || item.ownedBy || item.ownerName || item.createdBy || "N/A"}
                                    </p>
                                  </div>
                                </div>
                                <div className="videos-section">
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                    <path fill="#2b3674" d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                                  </svg>
                                  <div className="year-desc"><p>{item.video?.length || item.videosCount || item.videoCount || 0} videos</p></div>
                                </div>
                                <div className="shared-with-staff">
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="24" width="24">
                                    <path fill="#2b3674" d="M4 22.0001v-14.525c0 -0.4125 0.146915 -0.76565 0.44075 -1.0595 0.293665 -0.29365 0.64675 -0.4405 1.05925 -0.4405h9c0.4125 0 0.76565 0.14685 1.0595 0.4405 0.29365 0.29385 0.4405 0.647 0.4405 1.0595v14.525l-6 -2.975 -6 2.975Zm14.5 -3.975v-14.55H7.1v-1.5H18.5c0.4125 0 0.76565 0.14683 1.0595 0.4405 0.29365 0.29383 0.4405 0.647 0.4405 1.0595v14.55h-1.5Z" />
                                  </svg>
                                  <div className="year-desc"><p>shared with {item.sharedWithTeachersOrAdminsCount || item.sharesCount || item.shareCount || 0} staff members</p></div>
                                </div>
                              </div>
                            </Col>
                          ))
                        ) : (
                          <Col lg={12}><NoDataMessage /></Col>
                        )}
                      </Row>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="mb-4 align-items-stretch">
              <Col xxl={5} lg={5} className="mt-2 display-table">
                <div className="table-cell shadow-sm" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", height: "100%", padding: "15px 20px" }}>
                  <div className="results-sec">
                    <div className="recently-active-chart">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="d-flex align-items-center gap-3">
                          <h2 className="f-24 m-0" style={{ color: "#2B3674", fontSize: "24px" }}>Recently Active Staff</h2>
                          <div className="d-flex align-items-center gap-2" style={{ color: "#2B3674", opacity: 0.8 }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" />
                            </svg>
                            <span style={{ fontSize: "14px", fontWeight: "600" }}>{recentStaff.length} teachers total</span>
                            <div className="d-flex align-items-center gap-1 ms-2">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={sortOrder === "asc" ? "#D5008D" : "#2B3674"}
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ cursor: "pointer" }}
                                onClick={() => setSortOrder("asc")}
                              >
                                <path d="M12 19V5M5 12l7-7 7 7" />
                              </svg>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={sortOrder === "desc" ? "#D5008D" : "#2B3674"}
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ cursor: "pointer" }}
                                onClick={() => setSortOrder("desc")}
                              >
                                <path d="M12 5v14M5 12l7 7 7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="Review-history mt-4 scrollable-card-list" style={{ maxHeight: "600px", overflowY: "auto", overflowX: "hidden", paddingRight: "5px" }}>
                        {isLoading || organisationDashboardRecentActiveStaffStatus === "loading" ? (
                          <LoadingMessage message="Fetching active staff..." />
                        ) : recentStaff.length > 0 ? (
                          <Row className="mx-0">
                            {recentStaff.map((item, index) => (
                              <Col lg={6} md={12} className="mb-3" key={`${item._id}-${index}`}>
                                <div
                                  className="staff-card shadow-sm p-2 d-flex align-items-center gap-3"
                                  style={{
                                    cursor: "pointer",
                                    background: "#FFFFFF",
                                    borderRadius: "20px",
                                    border: "1px solid rgba(244, 247, 254, 1)",
                                    transition: "all 0.3s ease"
                                  }}
                                  onClick={() => navigate(`/organisations/staff-detail/${item._id}/${item.accountType}`)}
                                >
                                  <div className="avatar-wrapper" style={{ flexShrink: 0 }}>
                                    {item.image ? (
                                      <img
                                        src={item.image}
                                        alt=""
                                        style={{ width: "50px", height: "50px", borderRadius: "15px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div style={{
                                        width: "50px",
                                        height: "50px",
                                        background: "rgba(43, 54, 116, 0.05)",
                                        borderRadius: "15px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#2B3674",
                                        fontWeight: "700",
                                        fontSize: "16px"
                                      }}>
                                        {(item.firstName?.charAt(0) || "")}{(item.surName?.charAt(0) || item.lastName?.charAt(0) || "")}
                                      </div>
                                    )}
                                  </div>
                                  <div className="staff-info overflow-hidden">
                                    <h6 className="m-0 text-truncate" style={{ fontSize: "16px", color: "#2B3674", fontWeight: "700" }}>
                                      {item.firstName} {item.surName || item.lastName}
                                    </h6>
                                    <p className="m-0" style={{ color: "#A3AED0", fontSize: "13px", fontWeight: "500" }}>
                                      {item.lastLoggedIn ? new Date(item.lastLoggedIn).toLocaleString('en-US', {
                                        month: 'numeric',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      }).replace(',', '') : "N/A"}
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <NoDataMessage />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col xxl={7} lg={7} className="mt-2 display-table">
                <div className="table-cell shadow-sm" style={{ background: "#ffffff66", borderRadius: "20px", border: "1px solid #F4F7FE", height: "100%", padding: "15px 20px" }}>
                  <h2 className="f-24 m-0 mb-4" style={{ color: "#2B3674", fontSize: "24px" }}>Most Shared Videos</h2>
                  <div className="Review-history mt-3">
                    {isLoading || organisationDashboardVideoViewStatus === "loading" ? (
                      <LoadingMessage message="Fetching most shared videos..." />
                    ) : organisationDashboardVideoViewStatus === "failed" ? (
                      <div className="text-center p-4">
                        <p className="data-not-found-cmn">Unable to load videos. Please try again later.</p>
                      </div>
                    ) : videoData.length > 0 ? (
                      <CustomCarousel
                        videoData={videoData}
                        type="Staff"
                        redirection={true}
                      />
                    ) : (
                      <NoDataMessage />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </OrganisationLayout>
  );
}

export default memo(OrganisationGroupsDashboard);
